export const getEntityId = (entity) => entity?._id || entity?.id || entity;

export const getNombreCompleto = (entity, fallback = "Sin nombre") => {
    if (!entity || typeof entity !== "object") {
        return fallback;
    }

    return [entity.nombre, entity.apellido].filter(Boolean).join(" ") || entity.name || fallback;
};

export const formatTurnoDate = (value) => {
    if (!value) {
        return { fecha: "-", hora: "-" };
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return { fecha: String(value), hora: "-" };
    }

    return {
        fecha: date.toLocaleDateString("es-AR", { weekday: "short", day: "numeric", month: "short" }),
        hora: date.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }),
    };
};

export const formatDireccion = (direccion) => {
    if (!direccion) {
        return "Sin direccion";
    }

    if (typeof direccion === "string") {
        return direccion;
    }

    const calle = direccion.direccion || direccion.calle;
    const localidad = [direccion.ciudad, direccion.provincia].filter(Boolean).join(", ");
    const codigoPostal = direccion.codigoPostal ? `CP ${direccion.codigoPostal}` : "";

    return [calle, localidad, codigoPostal].filter(Boolean).join(" - ") || "Sin direccion";
};

export const formatSede = (sede) => {
    if (!sede) {
        return "Sede sin asignar";
    }

    if (typeof sede === "string") {
        return sede;
    }

    return sede.nombre || formatDireccion(sede.direccion);
};

export const normalizeTurno = (turno) => {
    const { fecha, hora } = formatTurnoDate(turno.fechaHora);
    const servicios = Array.isArray(turno.servicios) ? turno.servicios : [];
    const medico = turno.id_medico;
    const sede = turno.sede;

    return {
        id: getEntityId(turno),
        medico: getNombreCompleto(medico, "Medico sin asignar"),
        especialidad: servicios.map((servicio) => servicio?.nombre).filter(Boolean).join(", ") || "Sin servicio",
        fecha,
        hora,
        fechaHora: turno.fechaHora,
        fechaHoraPropuesta: turno.fechaHoraPropuesta ?? null,
        propuestoPor: turno.propuestoPor ?? null,
        sede: formatSede(sede),
        estado: String(turno.estado || "").toLowerCase(),
        pacienteId: getEntityId(turno.paciente),
        raw: turno,
    };
};

export const normalizeMedico = (medico) => ({
    id: getEntityId(medico),
    nombre: getNombreCompleto(medico),
    especialidad:
        medico.especialidad ||
        medico.servicios?.map((servicio) => servicio?.nombre).filter(Boolean).join(", ") ||
        "Sin especialidad",
    sedes: medico.sedes?.map(formatSede).filter(Boolean) || [],
    rating: medico.rating || "-",
});

export const normalizeSede = (sede, medicos = []) => {
    const sedeId = getEntityId(sede);
    const serviciosIds = new Set();
    medicos.forEach((medico) => {
        const medicoSedeIds = (medico.sedes || []).map(getEntityId);
        if (!medicoSedeIds.includes(sedeId)) return;
        (medico.servicios || []).forEach((servicio) => {
            const servicioId = getEntityId(servicio);
            if (servicioId) serviciosIds.add(servicioId);
        });
    });

    return {
        id: sedeId,
        nombre: sede.nombre || "Sede sin nombre",
        direccion: formatDireccion(sede.direccion),
        telefono: sede.telefono || "-",
        servicios: serviciosIds.size,
    };
};

export const normalizeServicio = (servicio, medicos = []) => {
    const servicioId = getEntityId(servicio);
    const medicosCount = medicos.filter((medico) =>
        (medico.servicios || []).some((s) => getEntityId(s) === servicioId)
    ).length;

    return {
        id: servicioId,
        nombre: servicio.nombre || "Servicio sin nombre",
        descripcion: servicio.descripcion || servicio.tipoServicio || "Sin descripcion",
        medicos: medicosCount,
    };
};

export const resolvePaciente = (pacientes, user) => {
    const userId = user?.pacienteId || user?.id || user?._id;
    const username = user?.username?.toLowerCase();
    const displayName = user?.displayName?.toLowerCase();

    return (
        pacientes.find((paciente) => {
            const pacienteId = getEntityId(paciente);
            const usuario = paciente.usuario;
            const usuarioId = getEntityId(usuario);
            const nombreCompleto = getNombreCompleto(paciente, "").toLowerCase();

            return (
                pacienteId === userId ||
                usuarioId === userId ||
                paciente.dni === user?.username ||
                usuario?.username?.toLowerCase() === username ||
                nombreCompleto === displayName
            );
        }) || null
    );
};

export const mSafe = (value) => String(value || "").toLowerCase();